import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PrinterIcon as Printer, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, ClockIcon as Clock, WarningCircleIcon as WarningCircle, PlugsConnectedIcon as PlugsConnected, InfoIcon as Info, ShieldWarningIcon as ShieldWarning } from "@phosphor-icons/react";

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("it-IT"); } catch { return s; }
};

export function PrintanistaConnectorCard() {
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadCfg = () => api.get("/integrations/printanista/config").then(({ data }) => setCfg(data)).catch(() => {});
  const loadLogs = () => api.get("/integrations/printanista/logs?limit=15").then(({ data }) => setLogs(data)).catch(() => {});
  useEffect(() => { loadCfg(); loadLogs(); }, []);

  const openModal = () => {
    setForm({
      base_url: cfg.base_url || "",
      account_id: cfg.account_id || "",
      public_api_key: "",
      username: "",
      password: "",
      enabled: cfg.enabled ?? false,
      verify_ssl: cfg.verify_ssl ?? true,
      timeout_seconds: cfg.timeout_seconds ?? 30,
    });
    setOpen(true);
  };
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, timeout_seconds: Number(form.timeout_seconds) || 30 };
      // stringa vuota non deve cancellare un segreto → rimuovi campi segreti vuoti
      ["public_api_key", "username", "password"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      const { data } = await api.put("/integrations/printanista/config", payload);
      setCfg(data);
      toast.success("Configurazione Printanista salvata");
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/printanista/test");
      if (data.ok) toast.success("Connessione riuscita", { description: data.message });
      else toast.error("Connessione fallita", { description: data.message });
      loadCfg(); loadLogs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!cfg) return null;

  const allSecrets = cfg.public_api_key_configured && cfg.username_configured && cfg.password_configured;
  let stato, badge;
  if (cfg.last_test_status === "success") { stato = "connesso"; badge = <Badge variant="outline" className="rounded-sm bg-green-100 text-green-800 border-green-200 gap-1"><CheckCircle size={13} weight="fill" /> Connesso</Badge>; }
  else if (cfg.last_test_status === "error") { stato = "errore"; badge = <Badge variant="outline" className="rounded-sm bg-red-100 text-red-800 border-red-200 gap-1"><XCircle size={13} weight="fill" /> Errore</Badge>; }
  else if (!cfg.public_api_key_configured && !cfg.username_configured) { stato = "non_configurato"; badge = <Badge variant="outline" className="rounded-sm bg-zinc-100 text-zinc-600 gap-1"><XCircle size={13} /> Non configurato</Badge>; }
  else if (!allSecrets) { stato = "incompleta"; badge = <Badge variant="outline" className="rounded-sm bg-amber-100 text-amber-800 border-amber-200 gap-1"><WarningCircle size={13} /> Configurazione incompleta</Badge>; }
  else { stato = "da_verificare"; badge = <Badge variant="outline" className="rounded-sm bg-blue-100 text-blue-800 border-blue-200 gap-1"><Clock size={13} /> Da verificare</Badge>; }

  const SecretState = ({ ok }) => ok
    ? <span className="text-green-700">Configurata</span>
    : <span className="text-muted-foreground">Non configurata</span>;

  return (
    <Card className="rounded-sm border-border shadow-none p-6" data-testid="printanista-connector-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Printer size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Printanista / FMAudit</h3>
            <p className="text-xs text-muted-foreground">Lettura account, dispositivi, contatori e consumabili tramite Printanista Public API (SOAP). Import dati in fase successiva.</p>
          </div>
        </div>
        {badge}
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Abilitato</span><span>{cfg.enabled ? "Sì" : "No"}</span></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Public API Key</span><span data-testid="prt-apikey-state"><SecretState ok={cfg.public_api_key_configured} /></span></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Username Web API</span><SecretState ok={cfg.username_configured} /></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Password Web API</span><SecretState ok={cfg.password_configured} /></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Ultimo test</span><span data-testid="prt-last-test">{fmtDate(cfg.last_test_at)}</span></div>
        <div className="flex justify-between border-b border-border/60 py-1"><span className="text-muted-foreground">Server</span><span className="font-mono text-xs truncate max-w-[200px]">{cfg.base_url || "—"}</span></div>
      </div>

      {cfg.needs_verification && (
        <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 mb-4 flex items-start gap-2" data-testid="prt-needs-verify">
          <WarningCircle size={15} className="mt-0.5 shrink-0" />
          <span>Configurazione migrata dai vecchi parametri: la Public API Key va verificata (potrebbe non essere una chiave Public API valida). Esegui "Verifica connessione".</span>
        </div>
      )}
      {cfg.last_test_status === "error" && cfg.last_test_message && (
        <div className="rounded-sm bg-red-50 border border-red-200 text-red-800 text-xs p-3 mb-4 flex items-start gap-2" data-testid="prt-last-error">
          <WarningCircle size={15} className="mt-0.5 shrink-0" />
          <div><span>{cfg.last_test_message}</span>{cfg.last_error_category && <span className="block text-[11px] opacity-70 mt-0.5">Categoria: {cfg.last_error_category}</span>}</div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button onClick={openModal} className="rounded-sm" data-testid="prt-configura-btn">Configura</Button>
        <Button variant="outline" onClick={test} disabled={testing} className="rounded-sm gap-2" data-testid="prt-test-btn">
          <PlugsConnected size={16} /> {testing ? "Verifica..." : "Verifica connessione"}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Associazione clienti e import dispositivi si gestiscono in <b>Clienti → Associazioni Printanista</b> e <b>Servizi → Dispositivi → Stampanti/Costo copia</b>.</p>

      {logs.length > 0 && (
        <div className="mt-5 border-t border-border pt-3" data-testid="prt-logs-section">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Log connessione</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr className="text-left border-b border-border/60">
                  <th className="py-1 pr-3 font-medium">Data/ora</th>
                  <th className="py-1 pr-3 font-medium">Operazione</th>
                  <th className="py-1 pr-3 font-medium">Stato</th>
                  <th className="py-1 pr-3 font-medium">HTTP</th>
                  <th className="py-1 pr-3 font-medium">Durata</th>
                  <th className="py-1 pr-3 font-medium">Categoria</th>
                  <th className="py-1 font-medium">Messaggio</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-border/40" data-testid="prt-log-row">
                    <td className="py-1 pr-3 whitespace-nowrap">{fmtDate(l.started_at)}</td>
                    <td className="py-1 pr-3">{l.soap_operation || l.action}</td>
                    <td className="py-1 pr-3">{l.status === "success"
                      ? <span className="text-green-700 flex items-center gap-1"><CheckCircle size={12} weight="fill" /> OK</span>
                      : <span className="text-red-700 flex items-center gap-1"><XCircle size={12} weight="fill" /> Errore</span>}</td>
                    <td className="py-1 pr-3">{l.http_status ?? "—"}</td>
                    <td className="py-1 pr-3 whitespace-nowrap">{l.duration_ms != null ? `${l.duration_ms} ms` : "—"}</td>
                    <td className="py-1 pr-3">{l.error_category || "—"}</td>
                    <td className="py-1 text-muted-foreground">{l.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="prt-config-modal">
          <DialogHeader>
            <DialogTitle>Configura Printanista / FMAudit</DialogTitle>
            <DialogDescription>Public API SOAP (namespace urn:printanista-v5). I segreti non vengono mai mostrati dopo il salvataggio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[62vh] overflow-y-auto pr-1">
            <div className="rounded-sm bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 flex items-start gap-2">
              <Info size={15} className="mt-0.5 shrink-0" />
              <span>La <b>Public API Key</b> (header <span className="font-mono">X-API-KEY</span>) è distinta dalle credenziali <b>Web API</b> username/password. La Public API Key si genera in <i>Administration → Settings → Printanista Hub Integration → Generate Key</i> o si richiede a ECI.</span>
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <span className="text-sm">Abilitato</span>
              <Switch data-testid="prt-f-enabled" checked={!!form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">URL server</Label>
              <Input data-testid="prt-f-base_url" value={form.base_url} onChange={setF("base_url")} placeholder="https://mds.fmwebaudit.com" className="mt-1 rounded-sm font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Public API Key</Label>
              <Input data-testid="prt-f-public_api_key" type="password" value={form.public_api_key} onChange={setF("public_api_key")} placeholder={cfg.public_api_key_configured ? "•••••• (invariata)" : "Chiave Printanista Hub Public API (header X-API-KEY)"} className="mt-1 rounded-sm font-mono text-sm" />
              <p className="text-[11px] text-muted-foreground mt-1">Chiave Printanista Hub Public API usata nell'header X-API-KEY.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Username Web API</Label>
              <Input data-testid="prt-f-username" type="password" value={form.username} onChange={setF("username")} placeholder={cfg.username_configured ? "•••••• (invariato)" : ""} className="mt-1 rounded-sm font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Password Web API</Label>
              <Input data-testid="prt-f-password" type="password" value={form.password} onChange={setF("password")} placeholder={cfg.password_configured ? "•••••• (invariata)" : ""} className="mt-1 rounded-sm font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Account ID iniziale (opzionale)</Label>
              <Input data-testid="prt-f-account_id" value={form.account_id} onChange={setF("account_id")} className="mt-1 rounded-sm font-mono text-sm" />
              <p className="text-[11px] text-muted-foreground mt-1">Non obbligatorio per il primo test. Valore non segreto.</p>
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm p-3">
              <div className="text-sm">Verifica SSL <span className="block text-xs text-muted-foreground">Attiva di default</span></div>
              <Switch data-testid="prt-f-verify_ssl" checked={!!form.verify_ssl} onCheckedChange={(v) => setForm((f) => ({ ...f, verify_ssl: v }))} />
            </div>
            {!form.verify_ssl && (
              <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2.5 flex items-start gap-2">
                <ShieldWarning size={14} className="mt-0.5 shrink-0" /><span>Verifica SSL disattivata (solo debug temporaneo).</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annulla</Button>
            <Button className="rounded-sm" onClick={save} disabled={saving} data-testid="prt-save-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
